"use client";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/PlaidImportModal/PlaidImportOnboardingView";

import { Icon } from "@ospweb/sdk-ui";
import React from "react";
import { cn } from "utils";

import NextImage from "@components/NextImage";
import { MobileStackPrimaryButton } from "@components/MobileStackNavigator";

import type { PlaidImportOnboardingViewProps } from "./types";

const I18n = I18nUtil.createScoped(Translations);

const FEATURES = [
  {
    icon: "lock-alt" as const,
    title: "Bank-level encryption",
    description:
      "Plaid handles your sign-in with 256-bit encryption. Your password never reaches Ed.",
  },
  {
    icon: "show" as const,
    title: "Read-only access",
    description:
      "Ed can see your balances and transactions, but can't move money or place trades on your behalf.",
  },
  {
    icon: "check-shield" as const,
    title: "Your data, never sold",
    description:
      "Ed never sells or shares your information with advertisers. Disconnect anytime in Settings.",
  },
] as const;

export const PlaidImportOnboardingView: React.FC<
  PlaidImportOnboardingViewProps
> = ({ loading, binding, onContinue }) => (
  <div
    className="flex min-h-0 flex-1 flex-col"
    data-testid="plaid-import-onboarding-view"
  >
    <div className="flex flex-1 flex-col items-center px-8 pt-3">
      <div className="flex items-center gap-6">
        <div className="size-[72px] shrink-0 overflow-hidden rounded-xl">
          <NextImage
            src="/assets/images/ed-logo-onboarding.png"
            alt="EdWealth"
            width={72}
            height={72}
            className="size-full"
          />
        </div>
        <div className="flex h-[6px] w-[18px] items-center justify-center">
          <NextImage src="/assets/images/bind-loading-light.png" alt="" />
        </div>
        <div className="size-[72px] shrink-0 overflow-hidden rounded-xl">
          <NextImage
            src="/assets/images/plaid/plaid-logo-onboarding.webp"
            alt="Plaid"
            width={72}
            height={72}
            className="size-full"
          />
        </div>
      </div>

      <div className="mt-8 flex w-full max-w-none flex-col gap-4 md:max-w-[460px]">
        {FEATURES.map((feature) => (
          <div key={feature.title} className="flex gap-3">
            <div className="flex items-start py-1">
              <Icon
                name={feature.icon}
                size={16}
                className="text-content_primary"
              />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <p className="title_l text-content_primary">{feature.title}</p>
              <p className="body_m text-content_primary">
                {feature.description}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>

    <div className="flex shrink-0 flex-col gap-4 px-4 py-6">
      <p className="body_s text-content_tertiary">
        {I18n.t(
          'By selecting "Continue", you agree to the Alnvest user terms, Privacy Policy and Third Party Brokerage Disclaimer',
        )}
      </p>
      <MobileStackPrimaryButton
        className={cn("w-full")}
        loading={loading}
        disabled={binding}
        onClick={onContinue}
      >
        {!binding ? (
          <NextImage
            src="/assets/images/plaid/plaid-logo-white.webp"
            alt=""
            width={20}
            height={20}
            className="shrink-0"
          />
        ) : null}
        {binding ? (
          <Icon
            name="ic_loading2"
            size={20}
            className="animate-spin text-content_inverse_primary"
          />
        ) : (
          "Continue with Plaid"
        )}
      </MobileStackPrimaryButton>
    </div>
  </div>
);
